import { LitElement, css, html } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import darkLightSvg from '../../assets/darklight.svg?raw'

export default class DarkLightToggle extends LitElement {
  static properties = {
    theme: { type: String, state: true }
  }

  static styles = css`
    :host {
      display: block;
      color: inherit;
    }

    button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: inherit;
    }

    .icon {
      width: 100%;
      height: 100%;
      display: block;
    }

    svg {
      width: 100%;
      height: 100%;
      display: block;
      color: inherit;
    }

    svg path {
      stroke: currentColor;
      fill: currentColor;
    }

    svg path:first-child {
      fill: none;
    }
  `

  constructor() {
    super()
    this.theme = this.getInitialTheme()
    this.applyTheme(this.theme)
    this.setupSystemPreferenceListener()

    // Remove the loading class to enable transitions after initial paint
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('theme-loading')
    })
  }

  getInitialTheme() {
    // Priority: localStorage > system preference > default light
    const stored = localStorage.getItem('theme-preference')
    if (stored === 'light' || stored === 'dark') {
      return stored
    }

    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }

    return 'light'
  }

  setupSystemPreferenceListener() {
    // Listen to system preference changes and always sync with OS
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      // Always sync with system preference changes
      const newTheme = e.matches ? 'dark' : 'light'
      this.theme = newTheme

      // Save the new system preference to localStorage
      localStorage.setItem('theme-preference', newTheme)

      // Apply to DOM
      this.applyTheme(newTheme)
    })
  }

  applyTheme(theme) {
    const html = document.documentElement

    if (theme === 'light' || theme === 'dark') {
      html.style.colorScheme = theme
      html.setAttribute('data-theme', theme)
    } else {
      html.style.colorScheme = 'light dark'
      html.removeAttribute('data-theme')
    }

    // Note: Transitions are now controlled by CSS classes (theme-loading)
  }

  toggleTheme() {
    // Toggle between light and dark
    this.theme = this.theme === 'light' ? 'dark' : 'light'

    // Save to localStorage
    localStorage.setItem('theme-preference', this.theme)

    // Apply to DOM
    this.applyTheme(this.theme)
  }

  render() {
    return html`
      <button
        @click=${this.toggleTheme}
        data-theme=${this.theme}
        aria-label="Toggle theme: ${this.theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}"
        title="${this.theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}"
      >
        <span class="icon">${unsafeHTML(darkLightSvg)}</span>
      </button>
    `
  }
}
